import React, { useEffect } from 'react'
import produce from 'immer'
import { isEmpty } from 'libraries/lodash'
import cx from 'classnames'
import Route from 'route-parser'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import LinkIcon from '@mui/icons-material/Link'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CloseIcon from '@mui/icons-material/Close'
import ErrorIcon from '@mui/icons-material/Error'
import ShareIcon from '@mui/icons-material/Share'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import IconButton from '@mui/material/IconButton'
import WordDownloadButton from 'components/shared/WordDownloadButton'
import { SHARE } from 'constants/controllers'
import url from 'libraries/front-end-core/core/url'
import { MAILTO } from 'libraries/front-end-core/constants/urls'
import { isValidResponse } from 'libraries/front-end-core/helpers/dataflow'
import { getBaseURI } from 'libraries/front-end-core/helpers/document'
import { notifyRender } from 'libraries/front-end-core/services/WindowService'
import { RESPONSE_PENDING } from 'libraries/front-end-core/constants/responses'
import notificationToast from 'helpers/notificationToast'
import TealToolTip from 'components/TealToolTip'
import ResponseConsumer from 'components/shared/ResponseConsumer'
import PrintButtonContainer from 'redux/containers/components/shared/PrintButtonContainer'
import PDFButtonContainer from 'redux/containers/components/shared/PDFButtonContainer'
import selectDeviceType from 'redux/selectors/selectDeviceType'
import useBaseState from 'hooks/useBaseState'

const mailParser = new Route(MAILTO)

const UrlLinkTextBox = ({ viewLink }) => (
  <div className='grow-1 flex pv1'>
    <input
      disabled
      type='text'
      value={viewLink}
      placeholder='Generating link...'
      className='w-100 bg-white shadow-none f6 b--black ba ph2 black'
    />
  </div>
)

const CopyToClipboardButton = ({ viewLink, short, notify }) => (
  <div className='pl2 pr4'>
    <TealToolTip title='Copy link to clipboard'>
      <IconButton
        size='small'
        disabled={short === RESPONSE_PENDING}
        data-clipboard-text={viewLink}
        data-event-category='Share'
        data-event-action='Copy'
        data-event-label={viewLink}
        onClick={() => {
          notify()
          viewLink && notificationToast(<LinkIcon />, toast.TYPE.SUCCESS, 'Link copied to clipboard')
        }}
        classes={{ root: 'black' }}
      >
        <LinkIcon />
      </IconButton>
    </TealToolTip>
  </div>
)

const CloseShareButton = ({ handleOnClick, viewLink }) => (
  <div className='pl2 pr4'>
    <IconButton
      size='small'
      onClick={handleOnClick}
      data-event-category='Share'
      data-event-action='Deselect'
      data-event-label={viewLink}
      classes={{ root: 'black' }}
    >
      <CloseIcon />
    </IconButton>
  </div>
)

const NavigateToSharedLinkButton = ({ viewLink }) => {
  return (
    <div className='pl2 pr4'>
      <TealToolTip title='Open link'>
        <IconButton
          size='small'
          disabled={!viewLink}
          onClick={() => window.open(viewLink, '_self')}
          data-event-category='Share'
          data-event-action='Go to'
          data-event-label={viewLink}
          classes={{ root: 'black' }}
        >
          <ArrowForwardIcon />
        </IconButton>
      </TealToolTip>
    </div>
  )
}

const Header = (props) => {
  const { baseState, setBaseState } = useBaseState(
    props.stateSource, { view: 'default', selectedIds: [], short: null }, [props.stateSource]
  )
  const { view, selectedIds, short, sectionContent } = baseState
  const setView = (view, e) => setBaseState(prevState => produce(prevState, draft => { draft.view = view }))

  useEffect(() => {
    notifyRender()
    setBaseState(prevState => produce(prevState, draft => { draft.view = 'default' }))
  // Was keyed on the whole `props` object - a fresh reference on every
  // parent render regardless of whether anything meaningful changed, so
  // this fired constantly and could force the share panel closed again
  // moments after a user opened it, if the parent happened to re-render in
  // between (confirmed live via debug trace: this is what caused the
  // legislation share flow to sometimes never show the generated link).
  // stateSource identity is the real "you're now looking at different
  // content" signal - the same one useBaseState's own subscription effect
  // above already keys on - so resetting to the default view only when
  // *that* changes preserves the intended behavior (don't leave a stale
  // share panel open across a genuine content change) without reacting to
  // incidental re-renders.
  }, [props.stateSource])

  const notify = () => {
    // CopyToClipboardButton is disabled while short === RESPONSE_PENDING, so
    // this can only fire for a genuine generation error.
    if (!isValidResponse(short)) {
      notificationToast(<ErrorIcon />, toast.TYPE.ERROR, 'Link generation error', 'bg-indian-red white')
    }
  }

  const renderShare = () => {
    const { aol } = props
    const viewLink = (isValidResponse(short) && url(SHARE, { aol, short }, {}, true)) || ''
    const show = !isEmpty(selectedIds)

    return (
      <div className='w-100 relative'>
        <div className='flex items-center justify-center'>
          <div
            className={cx(
              'bg-precedents-blue-20 transition-all w-100 w-100 overflow-hidden',
              {
                'max-h0': !show,
                'max-h3': show
              }
            )}
          >
            <div className='layout-width-constraint pv2 pv1-m'>
              <div className='f5'>
                <div className='flex'>
                  <UrlLinkTextBox {...{ viewLink }} />
                  <CopyToClipboardButton {...{ viewLink, short, notify }} />
                  <NavigateToSharedLinkButton {...{ viewLink, short }} />
                  <CloseShareButton {...{ viewLink }} handleOnClick={(e) => setView('default', e)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderDefault = () => {
    return (
      <ConnectedDefault
        {...{ ...props, ...baseState }}
        setView={(e) => setView('share', e)}
      />
    )
  }

  const getContent = () => (
    view === 'share' && (isValidResponse(short) || short === RESPONSE_PENDING)
      ? renderShare()
      : renderDefault()
  )

  return (
    <ResponseConsumer
      loadingComponent={null}
      baddataComponent={null}
      offlineComponent={null}
      pendingComponent={null}
      response={sectionContent}
      content={() => getContent()}
    />
  )
}

export default Header

const ConnectedDefault = (
  {
    selectedIds = [],
    short,
    aol,
    keyword,
    deselectAll,
    pdfUrl,
    dataHelpItem,
    displayPrint = true,
    displayPdf = true,
    displayWordDownload = true,
    setView
  }) => {
  const reviewLink =
    (isValidResponse(short) && url(SHARE, { aol, short }, {}, true)) ||
    ''
  const errorOccurred = !isValidResponse(short) && short !== RESPONSE_PENDING
  if (pdfUrl) {
    pdfUrl = (isValidResponse(short) && (getBaseURI() + new Route(pdfUrl).reverse({ aol, short }))) || null
  }
  const show = !isEmpty(selectedIds)

  return (
    <div className='w-100 relative'>
      <div className='flex items-center justify-center'>
        <div
          className={cx(
            'bg-precedents-blue-20 transition-all w-100 w-100 overflow-hidden',
            {
              'max-h0': !show,
              'max-h3': show
            }
          )}
        >
          <div
            className='f5 layout-width-constraint pv2 pv1-m'
            data-help-item={dataHelpItem}
            data-help-position='bottom'
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center'>
                <CloseButton {...{ deselectAll, selectedIds, pdfUrl }} />
                <SelectedSectionToPublishInfo {...{ selectedIds, keyword }} />
              </div>
              <div className='flex items-center'>
                {!errorOccurred && <ShareLinkButton {...{ reviewLink, setView }} />}
                <EmailShareLinkButton {...{ reviewLink }} />
                {displayPrint && !errorOccurred && pdfUrl && <PrintButtonContainer {...{ pdfUrl }} />}
                {displayPdf && !errorOccurred && pdfUrl && <PDFButtonContainer pdfUrl={pdfUrl} />}
                {displayWordDownload && <WordDownloadButton {...{ selectedIds }} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const SelectedSectionToPublishInfo = ({ selectedIds, keyword }) => {
  const { isMobile } = useSelector(selectDeviceType)

  return (
    <div className={cx('flex items-center f6-t')}>
      {!isMobile && <span className='mr2'>You have selected</span>}
      <span className='b'>{selectedIds.length}</span>
      <span className='mh2'>{keyword(selectedIds.length)}</span>
      to share
    </div>
  )
}

const CloseButton = ({ deselectAll, selectedIds, pdfUrl }) => (
  <div className='mr2 mb1-m'>
    <TealToolTip title='Deselect All' placement='top' arrow={false} bgColor='dark'>
      <IconButton
        onClick={() => deselectAll(selectedIds)}
        data-event-category='Deselect'
        data-event-action='Deselect'
        data-event-label={pdfUrl}
        classes={{ root: 'black', sizeSmall: 'f3 f4-t f5-m' }}
        size='small'
      >
        <CloseIcon fontSize='inherit' className='vm' />
      </IconButton>
    </TealToolTip>
  </div>
)

const ShareLinkButton = ({ reviewLink, setView }) => (
  <div className='mlauto mh2 mh1-m'>
    <TealToolTip title='Share' placement='top' arrow={false} bgColor='dark'>
      <IconButton
        size='small'
        onClick={setView}
        data-event-category='Share'
        data-event-action='Link'
        data-event-label={reviewLink}
        classes={{ root: 'black', sizeSmall: 'f3 f4-t f5-m' }}
      >
        <ShareIcon fontSize='inherit' className='vm' />
      </IconButton>
    </TealToolTip>
  </div>
)

const EmailShareLinkButton = ({ reviewLink }) => {
  const subject = 'FromCounsel - Shared content'
  const body = `A colleague has shared some FromCounsel content with you: ${reviewLink}`
  const emailLink = 'mailto:' + mailParser.reverse({ subject, body }).replace('#', '%23')

  return (
    <div className='mh2 mh1-m'>
      <TealToolTip title='Email' placement='top' arrow={false} bgColor='dark'>
        <IconButton
          size='small'
          onClick={() => window.open(emailLink, '_self')}
          url={reviewLink && emailLink}
          data-event-category='Share'
          data-event-action='Email'
          data-event-label={reviewLink}
          classes={{ root: 'black', sizeSmall: 'f3 f4-t f5-m' }}
        >
          <MailOutlineIcon fontSize='inherit' className='vm' />
        </IconButton>
      </TealToolTip>
    </div>
  )
}
